import { Box, Flex, Text, useMediaQuery } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { ItemsSelect } from 'components/portfolios/ItemsSelect';
import { PortfolioItemExtended, PortfolioItemTreeNode } from 'types/portfolios';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { createPortfolioItemTree } from 'services/portfolios/service';
import { PCW_STEPS, stepsWithSelectedItems } from './constants';
import mappedItemTypes from 'services/items/mappedItemTypes';
import InfoPopover from 'components/forms/InfoPopover';
import { FormattedMessage } from 'react-intl';
import { useWizardState } from 'components/wizard/hooks/useWizardState';

export function PortfolioContents() {
    const [isDesktop] = useMediaQuery('(min-width: 769px)');
    const [expanded, setExpanded] = useState<string[]>([]);
    const { formData } = useWizardState();
    const { items } = useSelector((state: RootState) => state.portfolio);

    const contents: PortfolioItemTreeNode[] = useMemo(() => {
        const ids = new Set<string>(
            stepsWithSelectedItems?.flatMap((step) => {
                const data = formData?.[step]?.selected;
                if (data) {
                    return data?.map((item: PortfolioItemExtended) => String(item.id));
                }
                return [];
            })
        );

        // Add selected main market model id to contents list
        const mainMarketModelId = formData?.[PCW_STEPS.PCW_PORTFOLIO_BASICS_STEP_ID]?.mainMarketModel;
        ids.add(String(mainMarketModelId));

        const tree = createPortfolioItemTree(items);
        const filtered = tree
            ?.reduce((acc, node) => {
                if (node?.nodes?.some((child) => ids.has(child.id))) {
                    acc.push({
                        ...node,
                        nodes: node.nodes.filter((child) => ids.has(child.id)),
                    });
                }
                return acc;
            }, [] as PortfolioItemTreeNode[])
            .sort((a, b) => {
                // Prioritize "Market Price Model" to come first
                const aIsMarketPrice = a.properties?.typeId === mappedItemTypes.MARKET_PRICE_MODEL.id;
                const bIsMarketPrice = b.properties?.typeId === mappedItemTypes.MARKET_PRICE_MODEL.id;

                if (aIsMarketPrice && !bIsMarketPrice) return -1;
                if (!aIsMarketPrice && bIsMarketPrice) return 1;

                // If both are the same priority, sort alphabetically by name
                return (a.properties?.name || a.label || '').localeCompare(b.properties?.name || b.label || '');
            });

        return filtered;
    }, [formData, items]);

    useEffect(() => {
        setExpanded(contents.map((node) => node.id));
    }, [contents]);

    return (
        <Box>
            <Flex gap={2} mb={4} alignItems={'center'}>
                <Text fontSize="lg" fontWeight="semibold" color="black">
                    <FormattedMessage id="wizard_pcw_contents_title" />
                </Text>

                <InfoPopover
                    width={isDesktop ? '2xl' : 'sm'}
                    info={<FormattedMessage id="wizard_pcw_contents_description" />}
                />
            </Flex>
            {contents?.length > 0 ? (
                <ItemsSelect
                    nodes={contents}
                    showSelect={false}
                    includeSearch={true}
                    expanded={expanded}
                    setExpanded={setExpanded}
                />
            ) : (
                <Text>Empty</Text>
            )}
        </Box>
    );
}
export default PortfolioContents;
